import { useEffect, useMemo, useState } from 'react'

import {
  ActionIcon,
  Box,
  Button,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconSearch,
} from '@tabler/icons-react'
import { format } from 'date-fns'
import dayjs from 'dayjs'

import { usdFormatter } from '@repo/common/helpers/formatters'
import { useAdminAssets, useAssetPrices } from '@repo/common/queries/admin/fund'

import { TowerCard } from '@/components/ui/TowerCard'
import {
  column,
  TowerDataTable,
  type TowerDataTableAccessor,
  type TowerDataTableColumn,
} from '@/components/ui/TowerDataTable'

import classes from './AssetPricingPage.module.css'

interface ProcessedAssetData {
  id: string
  name: string
  category: string
  fission_ticker: string | null
  prices: Map<string, { price: number; valuation: number }>
}

const CATEGORY_OPTIONS = [
  { value: 'external_fund', label: 'External Fund' },
  { value: 'company', label: 'Company' },
]

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Assets' },
  { value: 'company', label: 'Companies' },
  { value: 'external_fund', label: 'External Funds' },
]

export function AssetPricingPage() {
  const [selectedDate, setSelectedDate] = useState<string>(
    dayjs().format('YYYY-MM-DD'),
  )
  const [assetFilter, setAssetFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<string | null>('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [loadingProgress, setLoadingProgress] = useState<{
    loaded: number
    total?: number
  } | null>(null)
  const itemsPerPage = 50

  const { data: allAssetsData, isLoading: isLoadingAssets } = useAdminAssets()
  const { data: assetPricesData, isLoading: isLoadingPrices } = useAssetPrices({
    onProgress: (loadedCount, totalCount) => {
      setLoadingProgress({ loaded: loadedCount, total: totalCount })
    },
  })

  useEffect(() => {
    if (!isLoadingPrices) {
      setLoadingProgress(null)
    }
  }, [isLoadingPrices])

  const { filteredData, paginatedData, totalPages } = useMemo(() => {
    if (!allAssetsData?.assets.nodes)
      return { filteredData: [], paginatedData: [], totalPages: 0 }

    let filteredAssets = allAssetsData.assets.nodes
    if (assetFilter !== 'all') {
      filteredAssets = filteredAssets.filter(
        (asset) => asset.category === assetFilter,
      )
    }

    if (searchQuery.trim()) {
      filteredAssets = filteredAssets.filter((asset) =>
        asset.name.toLowerCase().includes(searchQuery.toLowerCase().trim()),
      )
    }

    if (sortBy === 'name') {
      filteredAssets = filteredAssets.sort((a, b) => {
        const comparison = a.name.localeCompare(b.name)
        return sortDirection === 'asc' ? comparison : -comparison
      })
    } else if (sortBy === 'category') {
      filteredAssets = filteredAssets.sort((a, b) => {
        const comparison = a.category.localeCompare(b.category)
        return sortDirection === 'asc' ? comparison : -comparison
      })
    } else {
      filteredAssets = filteredAssets.sort((a, b) =>
        a.name.localeCompare(b.name),
      )
    }

    const pricingMap = new Map<string, Map<string, { price: number }>>()
    if (assetPricesData?.asset_prices.nodes) {
      assetPricesData.asset_prices.nodes.forEach((priceData) => {
        const assetId = priceData.asset.id

        if (!pricingMap.has(assetId)) {
          pricingMap.set(assetId, new Map())
        }

        pricingMap.get(assetId)!.set(priceData.date, {
          price: priceData.price_per_share,
        })
      })
    }

    const processedAssets = filteredAssets.map((asset) => ({
      id: asset.id,
      name: asset.name,
      category: asset.category,
      fission_ticker: asset.fission_ticker,
      prices: (asset.fission_ticker && pricingMap.get(asset.id)) || new Map(),
    }))

    const calculatedTotalPages = Math.ceil(
      processedAssets.length / itemsPerPage,
    )
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedAssets = processedAssets.slice(startIndex, endIndex)

    return {
      filteredData: processedAssets,
      paginatedData: paginatedAssets,
      totalPages: calculatedTotalPages,
    }
  }, [
    allAssetsData,
    assetPricesData,
    assetFilter,
    searchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortDirection,
  ])

  const dateColumns = useMemo(() => {
    const dates = []
    for (let i = 0; i < 7; i++) {
      dates.push(dayjs(selectedDate).subtract(i, 'day').toDate())
    }
    return dates
  }, [selectedDate])

  const getPriceForDate = (asset: ProcessedAssetData, date: Date): number => {
    const dateString = format(date, 'yyyy-MM-dd')
    const priceData = asset.prices.get(dateString)

    if (priceData) return priceData.price

    const sortedDates = Array.from(asset.prices.keys()).sort()
    if (sortedDates.length === 0) return 0

    const firstAvailableDate = sortedDates[0]!
    if (dateString < firstAvailableDate) return 0

    const mostRecentDate = sortedDates[sortedDates.length - 1]!
    const mostRecentPrice = asset.prices.get(mostRecentDate)

    return mostRecentPrice?.price || 0
  }

  // const getValuationForDate = (
  //   asset: ProcessedAssetData,
  //   date: Date,
  // ): number => {
  //   const dateString = format(date, 'yyyy-MM-dd')
  //   const priceData = asset.prices.get(dateString)
  //
  //   if (priceData) return priceData.valuation
  //
  //   // Only show valuation if date is on or after the first available price date
  //   const sortedDates = Array.from(asset.prices.keys()).sort()
  //   if (sortedDates.length === 0) return 0
  //
  //   const firstAvailableDate = sortedDates[0]!
  //   if (dateString < firstAvailableDate) return 0
  //
  //   // Extrapolate from most recent valuation if needed
  //   const mostRecentDate = sortedDates[sortedDates.length - 1]!
  //   const mostRecentPrice = asset.prices.get(mostRecentDate)
  //
  //   return mostRecentPrice?.valuation || 0
  // }

  const handlePreviousWeek = () => {
    setSelectedDate((prev) =>
      dayjs(prev).subtract(1, 'week').format('YYYY-MM-DD'),
    )
  }

  const handleNextWeek = () => {
    setSelectedDate((prev) => {
      const nextWeek = dayjs(prev).add(1, 'week')
      const today = dayjs()
      return nextWeek.isAfter(today)
        ? today.format('YYYY-MM-DD')
        : nextWeek.format('YYYY-MM-DD')
    })
  }

  const handleCopyTicker = (ticker: string) => {
    void navigator.clipboard.writeText(ticker)
  }

  const handleSort = (
    _columnIndex: number,
    accessor:
      | keyof ProcessedAssetData
      | TowerDataTableAccessor<ProcessedAssetData, unknown>
      | undefined,
  ) => {
    if (!accessor || typeof accessor === 'function') return

    const columnKey = accessor as string
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(columnKey)
      setSortDirection('asc')
    }
    setCurrentPage(1)
  }

  const columns: TowerDataTableColumn<ProcessedAssetData>[] = [
    column({
      accessor: 'name',
      title: 'Asset Name',
      width: '20%',
      sortable: true,
      render: (name: string, asset: ProcessedAssetData) => (
        <Group gap="xs" wrap="nowrap">
          <Text>{name}</Text>
          {asset.fission_ticker && (
            <Tooltip label={`Copy ticker: ${asset.fission_ticker}`} withArrow>
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => {
                  handleCopyTicker(asset.fission_ticker!)
                }}
              >
                <IconCopy size={12} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      ),
    }),
    column({
      accessor: 'category',
      title: 'Category',
      width: '10%',
      sortable: true,
      render: (category) => {
        const option = CATEGORY_OPTIONS.find((opt) => opt.value === category)
        return option?.label || category
      },
    }),
    ...dateColumns.map((date) => ({
      title: format(date, 'MMM d'),
      width: '10%',
      textAlign: 'right' as const,
      render: (_: unknown, asset: ProcessedAssetData) => {
        const price = getPriceForDate(asset, date)
        return price > 0 ? (
          <Stack gap={1}>
            <Text size="sm" fw={600}>
              {usdFormatter(price)}
            </Text>
          </Stack>
        ) : (
          <Text c="dimmed" size="sm">
            -
          </Text>
        )
      },
    })),
  ]

  if (isLoadingAssets || isLoadingPrices) {
    return (
      <Stack gap="xl">
        <TowerCard
          title="Asset Pricing"
          description="View historic pricing data for companies and external funds"
        >
          <Stack align="center" gap="xxxs">
            <Text>Loading assets and prices...</Text>
            <Group>
              <Loader variant="dots" size="sm" />
              <Text size="sm" c="dimmed">
                {loadingProgress &&
                  `${loadingProgress.loaded} data points loaded`}
              </Text>
            </Group>
          </Stack>
        </TowerCard>
      </Stack>
    )
  }

  return (
    <Stack gap="xl">
      <TowerCard
        title="Asset Pricing"
        description="View historical pricing data for companies and external funds"
      >
        <Paper p="md" withBorder mb="lg">
          <Stack gap="md">
            <TextInput
              placeholder="Search assets by name..."
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.currentTarget.value)
                setCurrentPage(1)
              }}
              leftSection={<IconSearch size={16} />}
              style={{ maxWidth: 400 }}
            />

            <Group justify="space-between" align="flex-end">
              <Group gap="md" align="flex-end">
                <Group gap="xs" align="flex-end">
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={handlePreviousWeek}
                    leftSection={<IconChevronLeft size={14} />}
                  >
                    Previous
                  </Button>
                  <DateInput
                    valueFormat="YYYY-MM-DD"
                    label="From"
                    placeholder="Select date"
                    value={selectedDate}
                    onChange={(value) => {
                      if (value) setSelectedDate(value)
                    }}
                    leftSection={<IconCalendar size={16} />}
                    clearable={false}
                    style={{ minWidth: 180 }}
                    maxDate={new Date()}
                  />
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={handleNextWeek}
                    rightSection={<IconChevronRight size={14} />}
                  >
                    Next
                  </Button>
                </Group>
                <Select
                  label="Asset Type"
                  placeholder="Select asset type"
                  value={assetFilter}
                  onChange={(value) => {
                    setAssetFilter(value || 'all')
                    setCurrentPage(1)
                  }}
                  data={FILTER_OPTIONS}
                  style={{ minWidth: 150 }}
                />
              </Group>
            </Group>
          </Stack>
        </Paper>

        <Box>
          <Group justify="space-between" align="center" mb="md">
            <Title order={3}>Pricing Data</Title>
            <Text size="sm" c="dimmed">
              Showing {paginatedData.length} of {filteredData.length} assets
            </Text>
          </Group>

          <TowerDataTable
            data={paginatedData}
            columns={columns}
            withTableBorder
            withColumnBorders
            highlightOnHover
            striped
            className={classes.pricing_table}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
          />

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination
                value={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
                size="sm"
              />
            </Group>
          )}
        </Box>
      </TowerCard>
    </Stack>
  )
}
